import { deleteApi, getApi } from 'apis';
import { PermissionContext } from 'components/Global/AuthLayout';
import TableActionHeader from 'components/Global/TableActionHeader';
import { SideDrawerWrapper } from 'components/Global';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import React, { useContext, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import AddCoaBody from './AddCoaBody';
import CoaTable from './Table';
import { CoaDetailResponse, CoaListResponse, CoaRecord } from './types';
import styles from './index.module.scss';

const CoaTables: React.FC = () => {
	const permissions = useContext(PermissionContext);
	const [coaData, setCoaData] = useState<CoaRecord[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [totalRecords, setTotalRecords] = useState(0);
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [drawerAnimation, setDrawerAnimation] = useState(false);
	const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editSelected, setEditSelected] = useState<CoaRecord | null>(null);
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [drawerTitle, setDrawerTitle] = useState('Add Chart of Accounts');

	const fetchCoaData = async () => {
		setIsLoading(true);
		try {
			const response = await getApi('/coa/getAllCoa', { limit, page });
			const { data, total } = response.data as CoaListResponse;
			setCoaData(data);
			setTotalRecords(total);
		} catch (error: any) {
			toastText(
				error?.response?.data?.message || 'Failed to fetch COA data',
				'error',
			);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchCoaData();
	}, [page, limit]);

	const paginationChangeHandler = (pageNo: number, pageSize: number) => {
		setPage(pageNo);
		setLimit(pageSize);
	};

	const openAddDrawer = () => {
		setEditSelected(null);
		setDrawerTitle('Add Chart of Accounts');
		setDrawerAnimation(true);
		setIsSideDrawerOpen(true);
	};

	const openEditDrawer = async (record: CoaRecord) => {
		try {
			const res = await getApi(`/coa/${record.id}`);
			const { data } = res.data as CoaDetailResponse;
			setEditSelected(data);
			setDrawerTitle('Edit Chart of Accounts');
			setDrawerAnimation(true);
			setIsSideDrawerOpen(true);
		} catch (err: any) {
			toastText(
				err?.response?.data?.message || 'Failed to load COA account',
				'error',
			);
		}
	};

	const closeDrawerByAnimation = () => {
		setDrawerAnimation(false);
	};

	const removeDrawerFromDom = () => {
		setIsSideDrawerOpen(false);
		setEditSelected(null);
	};

	const confirmDelete = (record: CoaRecord) => {
		setDeleteId(record.id);
		setIsModalOpen(true);
	};

	const handleDelete = async () => {
		if (!deleteId) return;
		try {
			setIsDeleteLoading(true);
			const res = await deleteApi(`/coa/${deleteId}`);
			toastText(res.data.message || 'COA deleted successfully', 'success');
			setIsModalOpen(false);
			setDeleteId(null);
			if (totalRecords > 1 && totalRecords % limit === 1 && page > 1) {
				setPage(page - 1);
			} else {
				fetchCoaData();
			}
		} catch (err: any) {
			toastText(
				err?.response?.data?.message || 'Failed to delete COA account',
				'error',
			);
		} finally {
			setIsDeleteLoading(false);
		}
	};

	return (
		<>
			<div className={styles['coa-table']}>
				<TableActionHeader title={'Chart of Accounts'}>
					<div className={styles['coa-table__action']}>
						{permissions.isAdmin && (
							<button
								className={`btn-blue ${styles['coa-table__action--button']}`}
								onClick={openAddDrawer}
								disabled={isLoading}
							>
								<p>Add Account</p>
							</button>
						)}
					</div>
				</TableActionHeader>
				<CoaTable
					data={coaData}
					loading={isLoading}
					paginationChangeHandler={paginationChangeHandler}
					totalRecords={totalRecords}
					currentPage={page}
					onEdit={openEditDrawer}
					onDelete={confirmDelete}
				/>
			</div>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerTitle}
					position="right"
					width="half"
				>
					<AddCoaBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						fetchCoa={fetchCoaData}
						editSelected={editSelected}
					/>
				</SideDrawerWrapper>
			)}
			<ConfirmDelete
				isModalOpen={isModalOpen}
				handleOk={handleDelete}
				handleCancel={() => {
					setIsModalOpen(false);
					setDeleteId(null);
				}}
				deleteHandler={handleDelete}
				isLoading={isDeleteLoading}
				deleteMessage="Are you sure you want to delete this chart of accounts entry?"
			/>
		</>
	);
};

export default CoaTables;
